/**
 * WebSocketContext — Global real-time connection for authenticated users.
 *
 * Features:
 *  - Single WS connection per session (not per component)
 *  - Exponential-backoff auto-reconnect (500ms → 1s → 2s → … max 8s)
 *  - Heartbeat (send "ping" every 20s) to prevent Nginx idle timeout
 *  - Pub/sub: components subscribe to specific event types
 *  - Admin users get an additional /api/ws/admin channel
 *  - Falls back gracefully when WS is unavailable (polling still works)
 *
 * Race-condition fixes (v2):
 *  - Null all WS handlers BEFORE calling close() → prevents async onclose from
 *    scheduling stale retry timers after the effect cleanup resets mountedRef.
 *  - Each connect function closes the previous WS (if any) before creating a new one
 *    to prevent orphaned socket accumulation on rapid re-renders.
 *  - Stale-closure guard: onclose compares `ws === wsRef.current` before retrying.
 */

import React, {
  createContext, useContext, useEffect, useRef, useCallback, useState
} from 'react';

const WebSocketContext = createContext(null);

/* ── Helpers ──────────────────────────────────────────────────────────── */
const WS_BASE = (() => {
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
  const host  = window.location.host;
  return `${proto}://${host}`;
})();

const PING_INTERVAL  = 20_000;   // 20s heartbeat
const INITIAL_DELAY  = 500;      // first reconnect after 500ms (fast recovery)
const MAX_DELAY      = 8_000;    // cap reconnect at 8s

/**
 * Safely close a WebSocket without triggering "closed before connection established"
 * console errors.  Nulls all handlers FIRST so that the async `onclose` callback
 * (which fires after this sync function returns) cannot schedule stale retries.
 */
function silentClose(ws) {
  if (!ws) return;
  // Detach all handlers before closing — prevents async callbacks from firing
  ws.onopen    = null;
  ws.onmessage = null;
  ws.onclose   = null;
  ws.onerror   = null;
  try { ws.close(); } catch {}
}

/**
 * Shows a non-blocking full-page overlay instead of a blocking alert().
 * Redirects to /login after 3 seconds so the user can read the message.
 */
function _showBannedBanner(message) {
  const overlay = document.createElement('div');
  overlay.setAttribute('role', 'alertdialog');
  overlay.setAttribute('aria-live', 'assertive');
  overlay.style.cssText = [
    'position:fixed', 'inset:0', 'z-index:99999',
    'display:flex', 'align-items:center', 'justify-content:center',
    'background:rgba(0,0,0,0.75)', 'padding:24px',
  ].join(';');

  const box = document.createElement('div');
  box.style.cssText = 'background:#fff;border-radius:12px;padding:32px 28px;max-width:400px;width:100%;text-align:center;box-shadow:0 20px 60px rgba(0,0,0,0.4)';

  const icon = document.createElement('div');
  icon.style.cssText = 'font-size:2.5rem;margin-bottom:12px';
  icon.textContent = '🔒';

  const heading = document.createElement('h2');
  heading.style.cssText = 'color:#dc2626;font-size:1.1rem;font-weight:700;margin:0 0 10px';
  heading.textContent = 'Tài khoản bị khóa';

  const msg = document.createElement('p');
  msg.style.cssText = 'color:#374151;font-size:0.9rem;margin:0 0 18px';
  msg.textContent = message;

  const redirect = document.createElement('p');
  redirect.style.cssText = 'color:#9ca3af;font-size:0.8rem';
  redirect.textContent = 'Đang chuyển hướng về trang đăng nhập…';

  box.append(icon, heading, msg, redirect);
  overlay.appendChild(box);
  document.body.appendChild(overlay);
  setTimeout(() => { window.location.href = '/login'; }, 3000);
}

/* ── Provider ─────────────────────────────────────────────────────────── */
export const WebSocketProvider = ({ children, user, isAuthenticated }) => {
  const subscribers = useRef(new Map());
  const userWsRef   = useRef(null);
  const adminWsRef  = useRef(null);
  const mountedRef  = useRef(true);
  const userDelay   = useRef(INITIAL_DELAY);
  const adminDelay  = useRef(INITIAL_DELAY);
  const userTimer   = useRef(null);
  const adminTimer  = useRef(null);
  const pingTimer   = useRef(null);
  const [connected, setConnected] = useState(false);

  /* ── Publish to subscribers ─────────────────────────────────────────── */
  const publish = useCallback((event, data) => {
    const run = (key) => {
      const set = subscribers.current.get(key);
      if (set) set.forEach(cb => { try { cb(data, event); } catch {} });
    };
    run(event);
    run('*');
  }, []);

  /* ── User WebSocket ──────────────────────────────────────────────────── */
  const connectUser = useCallback(() => {
    if (!mountedRef.current || !isAuthenticated) return;
    const token = localStorage.getItem('tg247_token');
    if (!token) return;

    // Close previous socket before creating a new one (no orphaned sockets)
    if (userWsRef.current) {
      silentClose(userWsRef.current);
      userWsRef.current = null;
    }

    const ws = new WebSocket(`${WS_BASE}/api/ws/user`);
    userWsRef.current = ws;

    ws.onopen = () => {
      // Guard: component unmounted between creation and open
      if (!mountedRef.current || ws !== userWsRef.current) { ws.close(); return; }
      ws.send(token);
    };

    ws.onmessage = (e) => {
      if (e.data === 'pong') return;
      try {
        const { event, data } = JSON.parse(e.data);
        if (!event || event === 'ping') return;
        if (event === 'authenticated') {
          userDelay.current = INITIAL_DELAY; // reset backoff on successful auth
          setConnected(true);
          clearInterval(pingTimer.current);
          pingTimer.current = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) ws.send('ping');
          }, PING_INTERVAL);
          return;
        }
        if (event === 'account_banned') {
          localStorage.removeItem('tg247_token');
          localStorage.removeItem('tg247_user');
          _showBannedBanner(data?.message || 'Tài khoản của bạn đã bị khóa.');
          return;
        }
        publish(event, data || {});
      } catch {}
    };

    ws.onclose = (evt) => {
      // Stale-closure guard: ignore if a newer socket has already taken over
      if (ws !== userWsRef.current) return;
      clearInterval(pingTimer.current);
      setConnected(false);
      if (!mountedRef.current || !isAuthenticated) return;
      // 4001 = invalid/expired token, 4003 = banned — stop retrying
      if (evt.code === 4001 || evt.code === 4003) return;
      userTimer.current = setTimeout(() => {
        userDelay.current = Math.min(userDelay.current * 2, MAX_DELAY);
        connectUser();
      }, userDelay.current);
    };

    ws.onerror = () => {
      // Let onclose handle the retry — just trigger the close
      if (ws.readyState !== WebSocket.CLOSED && ws.readyState !== WebSocket.CLOSING) {
        ws.close();
      }
    };
  }, [isAuthenticated, publish]);

  /* ── Admin WebSocket ─────────────────────────────────────────────────── */
  const connectAdmin = useCallback(() => {
    if (!mountedRef.current || !isAuthenticated) return;
    if (!['admin', 'super_admin'].includes(user?.role)) return;
    const token = localStorage.getItem('tg247_token');
    if (!token) return;

    // Close previous socket before creating a new one
    if (adminWsRef.current) {
      silentClose(adminWsRef.current);
      adminWsRef.current = null;
    }

    const ws = new WebSocket(`${WS_BASE}/api/ws/admin`);
    adminWsRef.current = ws;

    ws.onopen = () => {
      if (!mountedRef.current || ws !== adminWsRef.current) { ws.close(); return; }
      ws.send(token);
    };

    ws.onmessage = (e) => {
      if (e.data === 'pong') return;
      try {
        const { event, data } = JSON.parse(e.data);
        if (!event || event === 'ping') return;
        if (event === 'authenticated') {
          adminDelay.current = INITIAL_DELAY;
          return;
        }
        if (event === 'account_banned') {
          localStorage.removeItem('tg247_token');
          localStorage.removeItem('tg247_user');
          _showBannedBanner(data?.message || 'Tài khoản của bạn đã bị khóa.');
          return;
        }
        publish(event, data || {});
      } catch {}
    };

    ws.onclose = (evt) => {
      // Stale-closure guard
      if (ws !== adminWsRef.current) return;
      if (!mountedRef.current || !isAuthenticated) return;
      if (evt.code === 4001 || evt.code === 4003) return;
      adminTimer.current = setTimeout(() => {
        adminDelay.current = Math.min(adminDelay.current * 2, MAX_DELAY);
        connectAdmin();
      }, adminDelay.current);
    };

    ws.onerror = () => {
      if (ws.readyState !== WebSocket.CLOSED && ws.readyState !== WebSocket.CLOSING) {
        ws.close();
      }
    };
  }, [isAuthenticated, user?.role, publish]);

  /* ── Lifecycle ───────────────────────────────────────────────────────── */
  useEffect(() => {
    mountedRef.current = true;
    if (isAuthenticated) {
      connectUser();
      connectAdmin();
    }
    return () => {
      mountedRef.current = false;
      clearTimeout(userTimer.current);
      clearTimeout(adminTimer.current);
      clearInterval(pingTimer.current);
      // silentClose nulls all handlers BEFORE ws.close() so the async onclose
      // callback cannot fire and schedule spurious retry timers after cleanup.
      silentClose(userWsRef.current);
      silentClose(adminWsRef.current);
      userWsRef.current  = null;
      adminWsRef.current = null;
      setConnected(false);
    };
  }, [isAuthenticated, connectUser, connectAdmin]);

  /* ── Public API ──────────────────────────────────────────────────────── */
  const subscribe = useCallback((eventType, callback) => {
    if (!subscribers.current.has(eventType)) {
      subscribers.current.set(eventType, new Set());
    }
    subscribers.current.get(eventType).add(callback);
    return () => subscribers.current.get(eventType)?.delete(callback);
  }, []);

  const value = { subscribe, connected };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

/* ── Hook ─────────────────────────────────────────────────────────────── */
export const useWebSocket = () => {
  const ctx = useContext(WebSocketContext);
  if (!ctx) throw new Error('useWebSocket must be used inside WebSocketProvider');
  return ctx;
};

/**
 * Convenience hook: subscribe to one or more event types.
 * callback(data, eventType) is called whenever any matching event arrives.
 *
 * Example:
 *   useWSEvent(['transaction_updated', 'wallet_updated'], (data, evt) => { ... });
 */
export const useWSEvent = (events, callback) => {
  const { subscribe } = useWebSocket();
  const cbRef = useRef(callback);
  cbRef.current = callback;

  useEffect(() => {
    const types = Array.isArray(events) ? events : [events];
    const unsubs = types.map(t => subscribe(t, (data, evt) => cbRef.current(data, evt)));
    return () => unsubs.forEach(fn => fn());
  }, [subscribe, ...(Array.isArray(events) ? events : [events])]); // eslint-disable-line
};
